/**
 * Number formatting helpers for the admin UI.
 *
 * `compactNumber` keeps stat lines from overflowing their container on large
 * counts: instead of "1 234 567" (which is wider than a card and was spilling
 * past the right edge — see B11) it renders "1,2 млн". `fullNumber` gives the
 * exact grouped value, suitable for a hover tooltip next to the compact one.
 *
 * Both are locale-fixed to ru-RU — the admin console is Russian-only and we
 * want deterministic, testable output.
 */

const compactFormatter = new Intl.NumberFormat("ru-RU", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const fullFormatter = new Intl.NumberFormat("ru-RU");

function safe(n: number | null | undefined): number {
  return typeof n === "number" && Number.isFinite(n) ? n : 0;
}

/**
 * Compact, human-friendly count: 45 → «45», 1234 → «1,2 тыс.»,
 * 1234567 → «1,2 млн». Non-finite / nullish input is treated as 0.
 */
export function compactNumber(n: number | null | undefined): string {
  return compactFormatter.format(safe(n));
}

/**
 * Exact value with thousands grouping: 1234567 → «1 234 567».
 * Intended for a `title` tooltip alongside the compact form.
 * Non-finite / nullish input is treated as 0.
 */
export function fullNumber(n: number | null | undefined): string {
  return fullFormatter.format(safe(n));
}

const moneyFormatter = new Intl.NumberFormat("ru-RU", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Money stored in kopecks → rubles with the «₽» suffix and exactly two
 * decimals: 12540 → «125,40 ₽», 0 → «0,00 ₽». Non-finite / nullish input is
 * treated as 0. Used by AI-spend and any kopeck-denominated amount that should
 * render to the operator.
 */
export function formatKopecks(kopecks: number | null | undefined): string {
  return `${moneyFormatter.format(safe(kopecks) / 100)} ₽`;
}
